import clsx from 'clsx';
import type { ReactNode } from 'react';
import { BiChevronLeft } from 'react-icons/bi';

import s from './SplitLayout.module.scss';

export type SplitLayoutProps = {
  /** The master column: identity, then the section list. */
  aside: ReactNode;
  /** The detail column. Render it always — on desktop the default section opens beside the list. */
  children: ReactNode;
  /**
   * A section was opened rather than defaulted (`?tab=` names one). Folded, the list shows
   * alone until then and gives way to the section once it is.
   */
  open?: boolean;
  /**
   * Re-keys the detail column so switching section starts it fresh rather than
   * carrying the previous one's scroll and transition state.
   */
  sectionKey?: string;
  /** Label of the folded back link — the open section's own title. */
  backLabel?: string;
  onBack?: () => void;
  /**
   * Folded, keep the master column above the detail rather than one at a time — for an
   * identity aside, or a section list that turns into pills there, as a vessel's does.
   */
  stacked?: boolean;
  className?: string;
};

/**
 * The master–detail layout: a sticky column beside the detail, folding at
 * `SPLIT_BREAKPOINT` (useSplitFolded.ts) to a list that a section opens over — or,
 * `stacked`, to the two on top of each other. Screens differ in what they put in the
 * two columns, never in how the columns behave.
 */
export const SplitLayout = ({
  aside,
  children,
  open,
  sectionKey,
  backLabel,
  onBack,
  stacked,
  className,
}: SplitLayoutProps) => (
  <div className={clsx(s.root, stacked && s.stacked, open && s.open, className)}>
    <div className={s.layout}>
      <div className={s.sidebarCol}>{aside}</div>

      <div className={s.detail} key={sectionKey}>
        {/* Folded shows one column at a time, so the section needs a way back
            to the list it was opened from. */}
        {onBack && (
          <button type="button" className={s.mobileBack} onClick={onBack}>
            <BiChevronLeft aria-hidden="true" />
            {backLabel}
          </button>
        )}
        {children}
      </div>
    </div>
  </div>
);
